import React, { FocusEvent, useState, useEffect } from 'react';

import { useSelector } from 'react-redux';

import { useGoogleAnalyticsEventContext, GoogleAnalyticsConstants } from '@vvoyages/google-analytics';
import classNames from 'classnames';
import dynamic from 'next/dynamic';

import useResourcesData from '@/app/[locale]/auth/v1/(base)/iframe/(tabs)/signup/useResourcesData';
import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import DateOfBirth from '@/components/DateOfBirth';
import NotificationError from '@/components/NotificationError';
import SignInInput from '@/components/SignIn/controls/SignInInput';
import { GoogleLoginButton } from '@/components/SignIn/GoogleLoginButton';
import signInStyles from '@/components/SignIn/SignIn.module.scss';
import Svg from '@/components/Svg';
import { isAgeValidForSignup } from '@/helpers/dateUtil';
import { hasFieldError, validateField } from '@/helpers/signUpUtils';
import { selectFeatureFlags } from '@/reducers/common/selectors';

import AppleLoginButton from './AppleLoginButton';
import defaultContent from './defaultContent';
import styles from './SignUp.module.scss';
import VirginFamilySubscription from '../VirginFamilySubscription/VirginFamilySubscription';

const FacebookLogin = dynamic(() => import('react-facebook-login'), { ssr: false });

type SignUpProps = {
  formFields?: any;
  formFieldErrorMessages?: any;
  serverErrorMsg?: string | string[];
  showErrorBox?: boolean;
  redirectURL?: string;
  settingData?: {
    appleClientId?: string;
    googleAppId?: string;
    facebookId?: string;
    formValidations?: any;
  };
  content?: any;
  isSignUpButtonDisabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  handleSocialLogin?: (formFieldValues: any) => void;
  // eslint-disable-next-line no-unused-vars
  onSignUpSubmit?: (formState: any) => void;
  onChangeErrorStatus?: () => void;
  setCommsOptConsent?: Function;
  isShowVirginConsent?: boolean;
};

const SignUp = ({
  onSignUpSubmit = () => {},
  serverErrorMsg = [],
  showErrorBox = false,
  formFields = {},
  settingData = {},
  content = defaultContent,
  redirectURL = '',
  handleSocialLogin = () => {},
  isSignUpButtonDisabled = false,
  formFieldErrorMessages = {},
  onChangeErrorStatus = () => {},
  setCommsOptConsent = () => {},
  isShowVirginConsent = true,
}: SignUpProps) => {
  const googleAnalyticsEventContext = useGoogleAnalyticsEventContext();
  const { appleClientId, googleAppId, facebookId, formValidations } = settingData;
  const {
    signupWithGoogleHeading,
    signupWithFacebookHeading,
    signupCreateAccount,
    signupButton,
    firstNameHeading,
    lastnameHeading,
    preferredNameHeading,
    emailHeading,
    passwordHeading,
    passwordMessage,
    dobHeading,
    virginVoyagesNews,
    newsMessage,
    emailSubscription,
    signupWithEmail,
  } = content;

  const resourcesData = useResourcesData();
  const { isVirginRedConsentEnabled } = useSelector(selectFeatureFlags);
  const invalidTLDs = resourcesData?.invalidTLDs;

  const [passwordType, setPasswordType] = useState('password');
  const [showPassIcon, setShowPassIcon] = useState('show');
  const [isSignupFormOpen, setIsSignUpFormOpen] = useState(false);
  const [eyeBorder, setEyeBorder] = useState(false);
  const [isMinAgeError, setIsMinAgeError] = useState(false);
  const [formState, setFormState] = useState(formFields);
  const [errors, setErrors] = useState({
    firstname: '',
    lastname: '',
    preferredname: '',
    email: '',
    password: '',
    dob: '',
  });

  useEffect(() => {
    if (showErrorBox) {
      const labelArrToString = Array.isArray(serverErrorMsg)
        ? serverErrorMsg.map((elem) => elem.toLowerCase().replace(/ /g, '-')).join(',')
        : serverErrorMsg.toLowerCase().replace(/ /g, '-');
      googleAnalyticsEventContext.event(
        GoogleAnalyticsConstants.EVENT_ACTION.ERROR,
        `sign-up|${labelArrToString}`,
        [],
        {
          element_title: labelArrToString,
          element_type: 'error',
        },
      );
    }
  }, [showErrorBox, googleAnalyticsEventContext, serverErrorMsg]);

  useEffect(() => {
    if (!facebookId) return undefined;

    const facebookContainer = document?.getElementById('facebookButton');
    if (!facebookContainer) return undefined;

    const addAriaLabel = () => {
      const facebookButton = facebookContainer?.querySelector('span button');

      if (facebookButton && !facebookButton?.getAttribute('aria-label')) {
        facebookButton?.setAttribute('aria-label', 'Sign up with Facebook');
        return true;
      }
      return false;
    };

    if (addAriaLabel()) return undefined;

    const observer = new MutationObserver(() => {
      if (addAriaLabel()) {
        observer.disconnect();
      }
    });

    observer.observe(facebookContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    const timeout = setTimeout(() => {
      observer.disconnect();
    }, 5000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [facebookId]);

  const handleReceiveEmailNotificationsCheckBox = () => {
    setFormState({
      ...formState,
      receiveEmailNotification: !formState.receiveEmailNotification,
    });
  };

  const handleVirginFamilySubscription = (value: boolean) => {
    setCommsOptConsent(value);
  };

  const togglePasswordVisibility = () => {
    setEyeBorder(true);

    if (passwordType === 'password') {
      setPasswordType('text');
      setShowPassIcon('hide');
    } else {
      setPasswordType('password');
      setShowPassIcon('show');
    }
  };

  const onSignUpWithEmailClick = () => {
    setIsSignUpFormOpen(true);
  };

  const onFormSubmit = (event: any) => {
    event.preventDefault();
    onSignUpSubmit(formState);
  };

  const handleValidation = (key: string, value: any) => {
    const errorMessage = validateField(key, value, formValidations, invalidTLDs);

    if (errorMessage) {
      googleAnalyticsEventContext.event(
        GoogleAnalyticsConstants.EVENT_ACTION.ERROR,
        `sign-up|${errorMessage.toLowerCase().replace(/ /g, '-')}`,
        [],
        {
          element_title: errorMessage.toLowerCase().replace(/ /g, '-'),
          element_type: 'error',
        },
      );
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [key]: errorMessage,
    }));
  };

  const onInputChange = (key: string, value: any) => {
    if (showErrorBox && serverErrorMsg !== '') {
      onChangeErrorStatus();
    }

    setFormState({
      ...formState,
      [key]: value,
    });
    handleValidation(key, value);
  };

  const onInputBlur = (key: string, value: any) => {
    handleValidation(key, value);
  };

  const onDobChange = ({ value }: { value: any }) => {
    const isAgeError = !isAgeValidForSignup(value);

    setIsMinAgeError(isAgeError);
    setFormState({
      ...formState,
      dob: value,
    });
  };

  const hasErrors = (): boolean => Object.values(errors).some((error) => error !== '');

  const isFormFilled =
    formState.firstName && formState.lastName && formState.email && formState.password && formState.dob;

  const isSubmitButtonDisabled = !isFormFilled || isSignUpButtonDisabled || hasErrors() || isMinAgeError;

  const onSignUpWithFacebook = (sdkResponse: any) => {
    const {
      first_name: firstname,
      last_name: lastname,
      id: socialMediaId,
      email,
      birthday,
      picture: { data },
    } = sdkResponse;

    handleSocialLogin({
      ...formState,
      firstname,
      lastname,
      email,
      birthday,
      socialMediaId,
      photo: data?.url ?? '',
      type: 'facebook',
    });
  };

  const onSignUpWithApple = (socialMediaId: string, user: any) => {
    handleSocialLogin({
      ...formState,
      socialMediaId,
      firstname: user?.name?.firstName,
      lastname: user?.name?.lastName,
      email: user?.email,
      type: 'apple',
    });
  };

  return (
    <>
      <div className={signInStyles.socialButtons}>
        <div className={styles.socialButtonWrapper}>
          {googleAppId && (
            <GoogleLoginButton formState={formState} onLogin={handleSocialLogin} ariaLabel="Sign up with Google">
              {signupWithGoogleHeading}
            </GoogleLoginButton>
          )}
        </div>
        <div className={styles.socialButtonWrapper}>
          {facebookId && (
            <FacebookLogin
              appId={facebookId}
              autoLoad={false}
              redirectUri={redirectURL}
              fields="first_name, last_name, gender, picture, email, birthday"
              callback={onSignUpWithFacebook}
              onFailure={() => {}}
              cssClass={`${signInStyles.socialButton} ${signInStyles.facebookButton} socialButton facebookButton`}
              textButton={signupWithFacebookHeading}
              isMobile={false}
              scope="public_profile, email, user_birthday"
              icon={<Svg glyph="facebook" fill="#fff" width={20} height={20} iconId="facebook-btn" />}
            />
          )}
        </div>
        <div className={styles.socialButtonWrapper}>
          {appleClientId && (
            <AppleLoginButton
              clientId={appleClientId}
              handleAppleLogin={onSignUpWithApple}
              redirectURI={redirectURL}
              variant="SIGN_UP"
            />
          )}
        </div>
      </div>
      <p className={styles.separatorText}>{signupCreateAccount}</p>
      {!isSignupFormOpen && (
        <div className={classNames(styles.singInViaEmailButtonWrapper, 'basicAuthButtonWrapper')}>
          <Button
            type="button"
            onClick={onSignUpWithEmailClick}
            classWrapper="btn-gray-signup"
            className={classNames(styles.singInViaEmailButton, `basicAuthButton`)}
            trackAction=""
            trackLabel=""
          >
            {signupButton}
          </Button>
        </div>
      )}
      {showErrorBox && <NotificationError mainText="This page contains errors 🤦" subText={serverErrorMsg} icon />}
      {isSignupFormOpen && (
        <form onSubmit={onFormSubmit}>
          <div className={styles.inputWrapper}>
            <SignInInput
              name="firstName"
              type="text"
              // @ts-ignore
              onChange={(value) => onInputChange('firstName', value)}
              label={firstNameHeading}
              required
              autoFocus
              onBlur={(event: FocusEvent<HTMLInputElement>) => onInputBlur('firstname', event.target.value)}
              error={hasFieldError('firstname', errors, serverErrorMsg, formFieldErrorMessages)}
            />
            {errors.firstname && <p className={styles.errorText}>{errors.firstname}</p>}
          </div>
          <div className={styles.inputWrapper}>
            <SignInInput
              name="lastName"
              type="text"
              // @ts-ignore
              onChange={(value) => onInputChange('lastName', value)}
              onBlur={(event: FocusEvent<HTMLInputElement>) => onInputBlur('lastname', event.target.value)}
              label={lastnameHeading}
              required
              error={hasFieldError('lastname', errors, serverErrorMsg, formFieldErrorMessages)}
            />
            {errors.lastname && <p className={styles.errorText}>{errors.lastname}</p>}
          </div>
          <div className={styles.inputWrapper}>
            <SignInInput
              name="preferredName"
              type="text"
              // @ts-ignore
              onChange={(value) => onInputChange('preferredName', value)}
              onBlur={(event: FocusEvent<HTMLInputElement>) => onInputBlur('preferredname', event.target.value)}
              label={preferredNameHeading}
              error={hasFieldError('preferredname', errors, serverErrorMsg, formFieldErrorMessages)}
            />
            {errors.preferredname && <p className={styles.errorText}>{errors.preferredname}</p>}
          </div>
          <div className={styles.inputWrapper}>
            <SignInInput
              name="email"
              type="text"
              // @ts-ignore
              onChange={(value) => onInputChange('email', value)}
              onBlur={(event: FocusEvent<HTMLInputElement>) => onInputBlur('email', event.target.value)}
              label={emailHeading}
              required
              error={hasFieldError('email', errors, serverErrorMsg, formFieldErrorMessages)}
            />
            {errors.email && <p className={styles.errorText}>{errors.email}</p>}
          </div>
          <div className={styles.inputWrapper}>
            <SignInInput
              name="password"
              type={passwordType}
              // @ts-ignore
              onChange={(value) => onInputChange('password', value)}
              onBlur={(event: FocusEvent<HTMLInputElement>) => onInputBlur('password', event.target.value)}
              label={passwordHeading}
              required
              error={hasFieldError('password', errors, serverErrorMsg, formFieldErrorMessages)}
            />
            {errors.password && <p className={styles.errorText}>{errors.password}</p>}
            <div className={styles.showPasswordButtonWrapper}>
              <button
                type="button"
                aria-label="Show password"
                className={classNames(styles.showPasswordButton, {
                  [styles.showPasswordButtonWithBorder]: eyeBorder,
                })}
                onClick={togglePasswordVisibility}
              >
                <Svg
                  glyph={showPassIcon}
                  width={24}
                  height={24}
                  ariaLabel={showPassIcon}
                  iconId={showPassIcon}
                  fill="#8C8C8C"
                />
              </button>
            </div>
          </div>
          <div className={styles.passwordHint}>{passwordMessage}</div>
          <div className={classNames(styles.inputWrapper, styles.dobWrapper)}>
            <label htmlFor={dobHeading} className={styles.dobLabel}>{`${dobHeading}*`}</label>
            {/* @ts-ignore */}
            <DateOfBirth onChangeDob={onDobChange} isMinAgeError={isMinAgeError} />
          </div>
          <div>
            <h3 className={styles.newsHeading}>{virginVoyagesNews}</h3>
            <p className={styles.newsMessage}>{newsMessage}</p>
            <div className={styles.inputWrapper}>
              <Checkbox
                name="receiveNews"
                label={emailSubscription}
                onChange={handleReceiveEmailNotificationsCheckBox}
              />
            </div>
          </div>
          {isVirginRedConsentEnabled && isShowVirginConsent && (
            <VirginFamilySubscription onChange={handleVirginFamilySubscription} />
          )}
          <Button
            type="submit"
            className={styles.submitButton}
            classWrapper="btn-red"
            disabled={isSubmitButtonDisabled}
            variant="secondary"
            onClick={() => {}}
            trackAction=""
            trackLabel=""
          >
            {signupWithEmail}
          </Button>
        </form>
      )}
    </>
  );
};

export default SignUp;
